import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { ProtectedRoute } from './components/ProtectedRoute';
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import Landing from './components/Landing';
import Login, { LoginBase } from './components/Login';
import BusquedaGlobalResultados from './components/BusquedaGlobalResultados';
import store from './actions/store';
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Auth from './auth';
import MiniSpinner from './components/General/MiniSpinner';

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const [frontendConfigLoaded, setFrontendConfigLoaded] = useState(false);
    const [recaptchaKey, setRecaptchaKey] = useState(process.env.REACT_APP_SITE_KEY || '');
    const [recaptchaError, setRecaptchaError] = useState(false);

    useEffect(() => {
        // Busca sesion existente
        Auth.checkSession();
        setIsLoading(false);

        // Busca actividad para establecer estado inactivo
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        const updateActivity = () => Auth.updateActivity();

        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        // Revisa periodicamente la sesion
        const intervalId = setInterval(() => {
            Auth.checkSession();
        }, 60 * 1000); // Cada minuto

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
            clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadFrontendConfig = async () => {
            const envSecret = process.env.REACT_APP_AUTH_SHARED_SECRET || '';
            const winSecret = (typeof window !== 'undefined' && window.__AUTH_SHARED_SECRET__) ? window.__AUTH_SHARED_SECRET__ : '';
            const winEnvironment = (typeof window !== 'undefined' && window.__APP_ENVIRONMENT__) ? window.__APP_ENVIRONMENT__ : '';
            const needsRecaptcha = !recaptchaKey;
            const needsSecret = !envSecret && !winSecret;
            const needsEnvironment = !winEnvironment;

            if (!needsRecaptcha && !needsSecret && !needsEnvironment) {
                if (!cancelled) {
                    setFrontendConfigLoaded(true);
                }
                return;
            }

            try {
                const resp = await fetch('/api/config/frontend', { credentials: 'include' });
                if (!resp.ok) {
                    if (!cancelled) {
                        setRecaptchaError(needsRecaptcha);
                    }
                    return;
                }

                const data = await resp.json();

                if (needsRecaptcha) {
                    const key = data?.recaptchaSiteKey;
                    if (!cancelled && typeof key === 'string' && key.trim()) {
                        setRecaptchaKey(key.trim());
                    } else if (!cancelled) {
                        setRecaptchaError(true);
                    }
                }

                if (needsSecret) {
                    const sharedSecret = data?.authSharedSecret;
                    if (!cancelled && typeof sharedSecret === 'string' && sharedSecret.trim()) {
                        window.__AUTH_SHARED_SECRET__ = sharedSecret.trim();
                    }
                }

                const environmentName = data?.environmentName;
                if (!cancelled && typeof environmentName === 'string' && environmentName.trim()) {
                    window.__APP_ENVIRONMENT__ = environmentName.trim();
                }
            } catch {
                if (!cancelled) {
                    setRecaptchaError(needsRecaptcha);
                }
            } finally {
                if (!cancelled) {
                    setFrontendConfigLoaded(true);
                }
            }
        };

        loadFrontendConfig();
        return () => { cancelled = true; };
    }, [recaptchaKey]);

    if (isLoading || !frontendConfigLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <MiniSpinner />
            </div>
        );
    }

    const AppRoutes = (
        <BrowserRouter>
            <Routes>
                <Route path="/login" element={recaptchaKey ? <Login /> : <LoginBase />} />
                <Route path="/busqueda-global-resultados" element={<ProtectedRoute><BusquedaGlobalResultados /></ProtectedRoute>} />
                <Route path="/*" element={<ProtectedRoute><Landing /></ProtectedRoute>} />
            </Routes>
            <ToastContainer
                position="top-right"
                autoClose={4500}
                hideProgressBar={false}
                newestOnTop
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable={false}
                pauseOnHover
                limit={5}
                theme="light"
                toastClassName="app-toast"
            />
        </BrowserRouter>
    );

    return (
        <Provider store={store}>
            {recaptchaKey ? (
                <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey} scriptProps={{ appendTo: "body" }}>
                    {AppRoutes}
                </GoogleReCaptchaProvider>
            ) : (
                <>
                    {recaptchaError ? (
                        <div className="p-2" style={{ background: '#fff3cd', color: '#856404' }}>
                            reCAPTCHA no configurado (SiteKey vacía). Revisar `Recaptcha:SiteKey` en appsettings o `REACT_APP_SITE_KEY`.
                        </div>
                    ) : null}
                    {AppRoutes}
                </>
            )}
        </Provider>
    );
}

export default App;
